import { Button, Card, Section, Spinner } from '@memorizecards/ui';
import type { ReactNode } from 'react';
import { ApiError } from '../lib/api';

/** Estado de carregamento a página inteira. */
export function CarregandoPagina({ rotulo = 'Carregando…' }: { rotulo?: string }): ReactNode {
  return (
    <Section spacing="lg">
      <div
        className="flex flex-col items-center gap-4 py-20 text-mc-ink3"
        role="status"
        aria-live="polite"
      >
        <Spinner className="size-7 text-mc-accent" />
        <p className="mc-mono">{rotulo}</p>
      </div>
    </Section>
  );
}

/** Bloco de carregamento dentro de uma secção. */
export function Carregando({ className }: { className?: string }): ReactNode {
  return (
    <div
      className={`flex items-center justify-center gap-3 py-12 text-mc-ink3 ${className ?? ''}`}
      role="status"
      aria-live="polite"
    >
      <Spinner className="text-mc-accent" />
      <span className="mc-mono">Carregando…</span>
    </div>
  );
}

/**
 * Erro em linha.
 *
 * Mostra a mensagem que o servidor escreveu — são todas em português e
 * pensadas para o aluno. Só quando não há mensagem nenhuma é que aparece o
 * texto genérico.
 */
export function Erro({
  error,
  aoTentarNovamente,
}: {
  error: unknown;
  aoTentarNovamente?: () => void;
}): ReactNode {
  const mensagem =
    error instanceof ApiError
      ? error.message
      : error instanceof Error && error.message
        ? error.message
        : 'Não consegui carregar esta parte agora.';

  return (
    <Card className="border-mc-danger/40" padding="md" role="alert">
      <p className="text-mc-ink">{mensagem}</p>
      {aoTentarNovamente ? (
        <Button variant="ghost" size="sm" className="mt-4" onClick={aoTentarNovamente}>
          Tentar de novo
        </Button>
      ) : null}
    </Card>
  );
}

export function Vazio({
  titulo,
  descricao,
  acao,
}: {
  titulo: string;
  descricao?: string;
  acao?: ReactNode;
}): ReactNode {
  return (
    <div className="flex flex-col items-center gap-3 rounded-mc-card border border-dashed border-mc-line2 px-6 py-14 text-center">
      <p className="text-mc-card-title text-mc-ink">{titulo}</p>
      {descricao ? <p className="max-w-md text-sm text-mc-ink2">{descricao}</p> : null}
      {acao}
    </div>
  );
}

/** Cabeçalho padrão das páginas internas. */
export function TituloPagina({
  eyebrow,
  titulo,
  descricao,
  acoes,
}: {
  eyebrow?: string;
  titulo: string;
  descricao?: string;
  acoes?: ReactNode;
}): ReactNode {
  return (
    <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
      <div className="min-w-0 flex-1">
        {eyebrow ? <p className="mc-mono mc-eyebrow mb-2">{eyebrow}</p> : null}
        <h1 className="text-mc-h2 break-words">{titulo}</h1>
        {descricao ? (
          <p className="mt-2 max-w-2xl text-mc-body text-mc-ink2">{descricao}</p>
        ) : null}
      </div>
      {acoes ? <div className="flex flex-wrap gap-3">{acoes}</div> : null}
    </div>
  );
}
